import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
} from 'react';

import { useParams } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import _unionBy from 'lodash-es/unionBy';
import _uniq from 'lodash-es/uniq';

import { localDate } from '@iconik-io/core/src/date/format';
import { getUserLabel } from '@iconik-io/core/src/get_user_label';
import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';
import {
  type AssetsApprovalSchemaNullable,
  httpPostByObjectTypeByObjectIdSharesByShareIdUsers,
  httpPutByObjectTypeByObjectIdSharesByShareId,
} from '@iconik-io/iconik-api/src/services/assets';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import { selectAssetVersionsExtendedMap } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import { Icon } from '@iconik-io/uad';

import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import type { UserNameOption } from '../../components/select_field_custom_values/select_field_username_value/select_field_username_value';
import UserBubble from '../../components/user_bubble/user_bubble';
import { REVIEW_AND_APPROVE } from '../../constants/help_urls';
import {
  REVIEW_DELETE_REQUEST_MODAL,
  REVIEW_SHARE_MODAL,
} from '../../constants/modals';
import { email, uuid } from '../../constants/regex';
import type { VariantType } from '../../constants/variants';
import {
  deleteExternalUserFromReview,
  deleteUserFromReview,
  updateAssetReviewRequest,
} from '../../ducks/asset_review_approvals/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import { getShareUsers } from '../../ducks/shares_users/thunks';
import { askForBasicUsersByIds } from '../../ducks/user/actions';
import {
  selectCurrentVersionId,
  selectIsLatestVersion,
} from '../../ducks/version/selectors';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Alert from '../../ui/alert/alert';
import Button from '../../ui/button/button';
import SelectUsersFieldWrapper from '../../ui/forms/field_wrappers/select_users/select_users';
import Form from '../../ui/forms/form';
import IconButton from '../../ui/icon_button/icon_button';
import TableComponent from '../../ui/table_simple/table_component/table_component';
import { selectUserRoles } from '../../utils/get_user_roles';
import { addObjectType } from '../../utils/object_type';
import ReviewShareForm from './review_share_form';

import styles from './asset_review.module.css';

type VariantObjectType = {
  APPROVED: VariantType;
  NOT_APPROVED: VariantType;
  REQUESTED: VariantType;
  'N/A': VariantType;
  MIXED: VariantType;
};

const singleReviewerVariants: VariantObjectType = {
  APPROVED: 'success',
  NOT_APPROVED: 'danger',
  REQUESTED: 'gray',
  'N/A': 'gray',
  MIXED: 'warning',
};

const multipleReviewerVariants: VariantObjectType = {
  APPROVED: 'success',
  NOT_APPROVED: 'danger',
  REQUESTED: 'gray',
  'N/A': 'gray',
  MIXED: 'danger',
};

const singleReviewerMessages = {
  APPROVED: 'Approved',
  NOT_APPROVED: 'Rejected',
  REQUESTED: 'Waiting for approval',
  'N/A': 'Waiting for approval',
  MIXED: 'Approved and rejected',
};

const multipleReviewerMessages = {
  APPROVED: 'Approved by all users',
  NOT_APPROVED: 'Rejected. All users must approve',
  REQUESTED: 'All users must approve',
  'N/A': 'Waiting for approval',
  MIXED: 'Rejected. All users must approve',
};

const ReviewStatusIcon = ({
  reviewer,
}: {
  reviewer: AssetsApprovalSchemaNullable;
}): ReactElement => {
  if (reviewer.status === 'APPROVED') {
    return <Icon name="ra_approved" className={styles.success} />;
  }
  if (reviewer.status === 'NOT_APPROVED') {
    return <Icon name="ra_rejected" className={styles.danger} />;
  }
  return <Icon name="ra_approved" />;
};

type FormData = { users: UserNameOption[] };

const EMPTY_APPROVALS: AssetsApprovalSchemaNullable[] = [];

export function ReviewPanel(): ReactNode {
  const dispatch = useAppDispatch();
  const { versionId: routeVersionId } = useParams({ strict: false });

  const assetPage = useAppSelector((state) => state.pages.asset);
  const { asset, assetId } = assetPage;
  const currentVersionId = useAppSelector((state) =>
    selectCurrentVersionId(state, assetId, routeVersionId),
  );
  const reviewRequest = useAppSelector(
    (state) =>
      state.assetReviewApprovalsReducer.assetReviewRequests[
        `${assetId}_${currentVersionId}`
      ],
  );
  const reviewApprovals = useAppSelector(
    (state) =>
      state.assetReviewApprovalsReducer.assetReviewApprovals[
        `${assetId}_${currentVersionId}`
      ] ?? EMPTY_APPROVALS,
  );
  const share = useAppSelector((state) =>
    reviewRequest?.share_id
      ? state.entities.shares[reviewRequest.share_id]
      : undefined,
  );
  const version = useAppSelector((state) =>
    currentVersionId
      ? selectAssetVersionsExtendedMap(state, assetId)[currentVersionId]
      : undefined,
  );
  const isLatestVersion = useAppSelector((state) =>
    selectIsLatestVersion(state, assetId, routeVersionId),
  );
  const dateFormat = useAppSelector((state) => state.appReducer.dateFormat);
  const usersBasic = useAppSelector((state) => state.entities.users_basic);
  const roles = useAppSelector(selectUserRoles);
  const userId = useAppSelector((state) => state.authReducer.userData.id);
  const isAdmin = useAppSelector(
    (state) => state.authReducer.userData.is_admin,
  );

  // Reviewer names are fetched lazily; refetch whenever the request changes.
  useEffect(() => {
    if (reviewRequest?.users) {
      const usersToFetch = reviewRequest.requested_by
        ? reviewRequest.users.concat(reviewRequest.requested_by)
        : reviewRequest.users;
      dispatch(askForBasicUsersByIds(usersToFetch));
    }
  }, [reviewRequest]);

  const onRequestApproval = useCallback(
    (prefilledUsers?: UserNameOption[] | undefined) => {
      dispatch(
        openModal({
          id: REVIEW_SHARE_MODAL,
          contextItems: [addObjectType(asset, OBJECT_TYPE_ASSETS)],
          options: {
            prefilledUsers,
          },
        }),
      );
    },
    [asset],
  );

  const onAddReviewers = useCallback(
    (data: FormData, reset: () => void) => {
      if (!reviewRequest) {
        return;
      }
      // need to update share with new emails / externals
      const userValues = data.users.map((item) => item.value);
      const emailsValues = userValues.filter((value) => email.test(value));
      const userIdsValues = userValues.filter((value) => uuid.test(value));

      const users = reviewRequest.users
        ? _uniq(reviewRequest.users.concat(userIdsValues))
        : [];
      const externals = reviewRequest.externals
        ? _uniq(reviewRequest.externals.concat(emailsValues))
        : [];
      const minNumber = reviewRequest.min_number ?? 1;
      dispatch(
        updateAssetReviewRequest(asset.id, {
          externals,
          users,
          min_number: minNumber > 1 ? externals.length + users.length : 1,
        }),
      );

      // Old review requests have a share connected to them. In those cases
      // we need to add the new users to the share.
      if (share?.id) {
        dispatch(askForBasicUsersByIds(userIdsValues)).then(
          async (userEntities) => {
            if (share?.id) {
              const userEmails = userIdsValues
                .map((id) => userEntities[id].email)
                .filter(truthyPredicate);
              const emailsToAdd = emailsValues.concat(userEmails);
              await Promise.all(
                emailsToAdd.map((emailToAdd) =>
                  dispatch(
                    httpPostByObjectTypeByObjectIdSharesByShareIdUsers(
                      OBJECT_TYPE_ASSETS,
                      asset.id,
                      share.id,
                      { email: emailToAdd },
                    ),
                  ),
                ),
              );
              await dispatch(
                getShareUsers(OBJECT_TYPE_ASSETS, asset.id, share.id),
              );
            }
          },
        );
      } else {
        // New review requests do not have a share connected to them so we need to create a new share.
        onRequestApproval(data.users);
      }

      reset();
    },
    [reviewRequest, asset, share, onRequestApproval],
  );

  const onDeleteUser = useCallback(
    (targetUserId?: string) => {
      if (targetUserId && reviewRequest) {
        dispatch(deleteUserFromReview(reviewRequest, targetUserId));
      }
    },
    [reviewRequest],
  );

  const onDeleteExternal = useCallback(
    (userEmail?: string) => {
      if (userEmail && reviewRequest) {
        dispatch(deleteExternalUserFromReview(reviewRequest, userEmail));
      }
    },
    [reviewRequest],
  );

  const onDeleteApproval = useCallback(() => {
    dispatch(
      openModal({
        id: REVIEW_DELETE_REQUEST_MODAL,
        contextItems: [asset],
      }),
    );
  }, [asset]);

  const editShare = useCallback(
    (
      ...args: Parameters<typeof httpPutByObjectTypeByObjectIdSharesByShareId>
    ) => dispatch(httpPutByObjectTypeByObjectIdSharesByShareId(...args)),
    [],
  );

  const editReviewRequest = useCallback(
    (...args: Parameters<typeof updateAssetReviewRequest>) =>
      dispatch(updateAssetReviewRequest(...args)),
    [],
  );

  if (!reviewRequest) {
    return (
      <NoContentPanel
        title="Keep track of review and approval status for individual assets"
        linkText="Learn more"
        linkHref={REVIEW_AND_APPROVE}
        actionText="REQUEST APPROVAL"
        onActionClick={
          roles.canWriteApprovalRequest ? onRequestApproval : undefined
        }
      />
    );
  }

  const approvals = [
    ...reviewApprovals.map((reviewApproval) => ({
      ...reviewApproval,
      key: reviewApproval.user || reviewApproval.external,
    })),
  ];

  const users = reviewRequest.users ?? [];
  const externals = reviewRequest.externals ?? [];
  const reviewUsers = [
    ...users.map((user) => ({ user, key: user })),
    ...externals.map((external) => ({ external, key: external })),
  ];
  const reviewers = _unionBy(approvals, reviewUsers, 'key');
  let approvedCounter = 0;
  const allShouldApprove = (reviewRequest.min_number || 0) > 1;
  const currentUserIsOwner = reviewRequest.requested_by === userId;
  const canDelete =
    ((roles.canDeleteApprovalRequest && currentUserIsOwner) ||
      Boolean(isAdmin)) &&
    isLatestVersion;
  const canEdit =
    ((roles.canWriteApprovalRequest && currentUserIsOwner) ||
      Boolean(isAdmin)) &&
    isLatestVersion;

  const reviewersComponents = reviewers.map((reviewer) => {
    if (reviewer.user) {
      if (reviewer.status === 'APPROVED') {
        approvedCounter += 1;
      }
      const user = usersBasic[reviewer.user];
      return (
        <tr key={reviewer.user}>
          <td key="data" className={styles.user}>
            {user ? (
              <>
                <UserBubble src={user.photo} name={getUserLabel(user)} />
                <span>{getUserLabel(user)}</span>
                {canDelete && (
                  <IconButton
                    variant="danger"
                    iconName="deletion_delete"
                    className={styles.delete_icon}
                    onClick={(event) => {
                      event.preventDefault();
                      onDeleteUser(reviewer.user ?? undefined);
                    }}
                  />
                )}
              </>
            ) : (
              <span>--</span>
            )}
          </td>
          <td key="status" className={styles.status}>
            <ReviewStatusIcon reviewer={reviewer} />
          </td>
        </tr>
      );
    }
    if (reviewer.external) {
      if (reviewer.status === 'APPROVED') {
        approvedCounter += 1;
      }
      return (
        <tr key={reviewer.external}>
          <td className={styles.user}>
            <UserBubble name={reviewer.external} />
            <span>{reviewer.external}</span>
            {canDelete && (
              <IconButton
                iconName="deletion_delete"
                iconSizeUAD="m"
                className={styles.delete_icon}
                onClick={() => onDeleteExternal(reviewer.external ?? undefined)}
              />
            )}
          </td>
          <td className={styles.status}>
            <ReviewStatusIcon reviewer={reviewer} />
          </td>
        </tr>
      );
    }
    return undefined;
  });

  const requestedAt = formatDistanceToNow(
    localDate(reviewRequest.request_date ?? undefined),
    { addSuffix: true },
  );

  const userWhoRequested = reviewRequest.requested_by
    ? usersBasic[reviewRequest.requested_by]
    : null;

  const status = reviewRequest?.status;
  const variants = allShouldApprove
    ? multipleReviewerVariants
    : singleReviewerVariants;
  const messages = allShouldApprove
    ? multipleReviewerMessages
    : singleReviewerMessages;
  const alertVariant = variants[status || 'REQUESTED'];
  const alertTitle = messages[status || 'REQUESTED'];
  const stats =
    allShouldApprove && status === 'REQUESTED'
      ? `(${approvedCounter}/${users.length + externals.length})`
      : '';

  return (
    <div className={styles.root}>
      <Alert variant={alertVariant} title={`${alertTitle} ${stats}`} />
      {reviewRequest.request_date && (
        <Form<FormData> onValidSubmit={onAddReviewers}>
          {!isLatestVersion && version ? (
            <p>
              <span className={styles.version}>{version.name}</span> review
              status
            </p>
          ) : (
            userWhoRequested && (
              <p>
                Created {requestedAt} by &nbsp;
                {userWhoRequested.first_name} {userWhoRequested.last_name}
              </p>
            )
          )}
          <h3 className={styles.heading}>Reviewers</h3>
          {reviewersComponents.length > 0 ? (
            <TableComponent striped className={styles.table}>
              <tbody>{reviewersComponents}</tbody>
            </TableComponent>
          ) : (
            <p>No reviewers added to the request</p>
          )}
          {canEdit && (
            <SelectUsersFieldWrapper
              required
              isCreatorMode
              canClear
              isMulti
              name="users"
              validations="containsOnlyEmailsOrUUIDs"
              buttonAfter={
                <Button
                  className={styles.select_users_button}
                  variant="success"
                  type="submit"
                  iconName="user_add"
                  noBorder
                />
              }
            />
          )}
        </Form>
      )}
      {canEdit && share ? (
        <ReviewShareForm
          asset={asset}
          dateFormat={dateFormat}
          editShare={editShare}
          editReviewRequest={editReviewRequest}
          showMinNumber={false}
          reviewRequest={reviewRequest}
          onDeleteApproval={onDeleteApproval}
          canDelete={canDelete}
          share={share}
          roles={roles}
        />
      ) : null}

      {canDelete && !share ? (
        <div className={styles.actions}>
          <Button
            onClick={onDeleteApproval}
            iconName="deletion_delete"
            variant="danger"
          >
            Delete Approval
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default ReviewPanel;
